import { useEffect } from "react";
import { Trash2, ShieldOff, Mail, ChevronRight } from "lucide-react";
import { Reveal } from "../components/site/Motion";
import { SectionLabel } from "../components/site/Bits";
import { SUPPORT_EMAIL } from "../lib/site";

const Li = ({ children }) => (
  <li className="flex gap-3">
    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-greenaccent" />
    <span>{children}</span>
  </li>
);

const Block = ({ heading, num, children, icon: Icon }) => (
  <div>
    <div className="flex items-center gap-3">
      {Icon && (
        <span className="grid h-10 w-10 place-items-center rounded-2xl bg-brand/5 text-brand">
          <Icon className="h-5 w-5" />
        </span>
      )}
      <h2 className="font-display text-xl font-bold text-ink">
        {num ? <span className="mr-2 text-brand">{num}.</span> : null}
        {heading}
      </h2>
    </div>
    <div className="mt-3 space-y-3 pl-0 text-slate-600 sm:pl-[52px]">{children}</div>
  </div>
);

export default function DeleteAccount() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Delete Your RenewTracker Account";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", "Learn how to permanently delete your RenewTracker account from within the app, what happens to your data, and how to contact support.");
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc !== null) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <main data-testid="delete-account-page" className="bg-background pt-28">
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="mesh-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-4xl px-5 py-16 lg:px-8">
          <Reveal>
            <SectionLabel>Account</SectionLabel>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-tighter text-ink sm:text-5xl">
              Delete Your RenewTracker Account
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              RenewTracker allows users to permanently delete their account directly from within the app. This page explains how to do it and what happens to your data.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <article className="space-y-12 text-[15px] leading-relaxed text-slate-700">
          <Block heading="Delete Your Account" icon={Trash2}>
            <p>RenewTracker allows users to permanently delete their account directly from within the app. Deletion is initiated and confirmed by you, and takes effect immediately.</p>
          </Block>

          <Block heading="How to Delete Your Account" icon={ChevronRight}>
            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand/10 text-xs font-bold text-brand">1</span>
                <span>Open the RenewTracker app.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand/10 text-xs font-bold text-brand">2</span>
                <span>Sign in to your account.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand/10 text-xs font-bold text-brand">3</span>
                <span>Go to <strong>Profile → Settings → Delete Account</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand/10 text-xs font-bold text-brand">4</span>
                <span>Confirm the deletion.</span>
              </li>
            </ol>
          </Block>

          <Block heading="What Happens When You Delete Your Account" icon={ShieldOff}>
            <ul className="space-y-2">
              <Li>Your RenewTracker account is permanently deleted.</Li>
              <Li>Your personal profile information is permanently removed.</Li>
              <Li>Your reminders, notification settings, and saved data are permanently deleted.</Li>
              <Li>You will no longer be able to access your account.</Li>
            </ul>
          </Block>

          <Block heading="Data Retention" icon={ShieldOff}>
            <p>Unless required by law, no personal data is retained after account deletion. Any legally required records are retained only for the minimum period required by applicable laws.</p>
          </Block>

          <Block heading="Need Help?" icon={Mail}>
            <p>If you experience any issues deleting your account, contact us at:</p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="delete-account-support-email"
              className="inline-flex items-center gap-2 rounded-full border border-brand/15 bg-white px-5 py-3 text-sm font-semibold text-brand shadow-sm transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand/5"
            >
              <Mail className="h-4 w-4" /> {SUPPORT_EMAIL}
            </a>
          </Block>
        </article>
      </section>
    </main>
  );
}
